export const FILE_BROWSER_FOLDERS = [
  { label: 'VA Forms', path: 'va_forms' },
  { label: 'Evidence', path: 'evidence' },
  { label: 'Mental Health Evidence', path: 'mental-health-evidence' },
  { label: 'Statements', path: 'statements' },
  { label: 'Medical Opinions', path: 'medical-opinions' },
  { label: 'Admin Folder', path: 'admin-folder', role: 'admin' },
  {
    label: 'Submission Proof',
    path: 'admin-folder/submission-proof',
    role: 'admin',
  },
];

// Extra folders only surfaced for users on the DBQ Files UI feature flag allowlist.
// `matchLocation` is a regex string matched against `file.fileLocation` and takes
// precedence over `fileMeta.fileDir` (used for DBQ previews whose stored
// `fileMeta.fileDir` is 'root' but whose path always contains the substring).
// `matchPrefix` matches files whose `fileMeta.fileDir` starts with the folder's path.
// `groupBy: 'packet'` tells the FileBrowser to render packet groupings inside.
// `systemManaged: true` disables drag-and-drop in/out so users can't accidentally
// orphan packet artifacts by moving them.
export const DBQ_FILES_UI_FOLDERS = [
  {
    label: 'DBQ Previews',
    path: 'dbq-previews',
    matchLocation: '/dbq-previews/',
    systemManaged: true,
  },
  {
    label: 'Exam Packets',
    path: 'dbq-submission-packets',
    matchPrefix: true,
    groupBy: 'packet',
    systemManaged: true,
  },
];

// Stable key for grouping/counting packet artifacts. Same fallback chain used
// by both FileBrowser folder count and PacketGroupList grouping so they agree.
export const getPacketKey = (file) =>
  file?.fileMeta?.userDbqId ||
  file?.fileMeta?.packetFolderName ||
  file?.fileMeta?.fileDir ||
  file?.fileId;

const escapeRegExp = (value) =>
  String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const toTitleCase = (value) =>
  String(value || '')
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export const getFileFolderPath = (file, folders = FILE_BROWSER_FOLDERS) => {
  const fileLocation = String(file?.fileLocation || '');

  // `matchLocation` rules override fileMeta.fileDir so we can route preview
  // files (whose stored fileDir is 'root') into DBQ Previews based on their
  // S3 path substring.
  const locationMatch = folders.find(
    (folder) =>
      folder.matchLocation &&
      new RegExp(folder.matchLocation).test(fileLocation),
  );
  if (locationMatch) return locationMatch.path;

  const fileDir = file?.fileMeta?.fileDir;
  if (typeof fileDir === 'string' && fileDir.trim()) {
    const trimmed = fileDir.trim();
    const exact = folders.find((folder) => folder.path === trimmed);
    if (exact) return exact.path;
    const prefix = folders.find(
      (folder) => folder.matchPrefix && trimmed.startsWith(`${folder.path}/`),
    );
    if (prefix) return prefix.path;
    return trimmed;
  }

  const matchedFolder = folders.find((folder) =>
    new RegExp(`/${escapeRegExp(folder.path)}(?:/|$)`).test(fileLocation),
  );

  return matchedFolder?.path || 'root';
};

export const getFileFolderSegments = (file) => {
  const folderPath = getFileFolderPath(file);
  return folderPath === 'root' ? [] : folderPath.split('/').filter(Boolean);
};

export const getFileFolderLabel = (folderPath) => {
  if (!folderPath || folderPath === 'root') return 'Root';

  const exactMatch =
    FILE_BROWSER_FOLDERS.find((folder) => folder.path === folderPath) ||
    DBQ_FILES_UI_FOLDERS.find((folder) => folder.path === folderPath);
  if (exactMatch?.label) return exactMatch.label;

  const segments = folderPath.split('/').filter(Boolean);
  return toTitleCase(segments[segments.length - 1] || folderPath);
};
